/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../../components/ui/dropdown-menu";
import { Bell, CheckCircle2, Circle, Eye, CheckCircle } from "lucide-react";
import { useUserNotifications } from "../../hooks/use-user-notifications";
import { toast } from "sonner";

interface NotificationDropdownProps {
  className?: string;
  variant?: "admin" | "user";
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  className = "",
  variant = "user",
}) => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    isLoading,
    loadMore,
    pagination,
    error,
  } = useUserNotifications({
    enableWebSocket: true,
    gcTime: 60 * 60 * 1000, // 1 hour cache
    staleTime: 60 * 60 * 1000, // 1 hour stale time
    refetchOnWindowFocus: false,
    enabled: true,
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
    } catch (error: any) {
      console.error("Error marking notification as read:", error);
      toast.error(error?.message || "Failed to mark notification as read");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setIsDropdownOpen(false);
    } catch (error: any) {
      console.error("Error marking all notifications as read:", error);
      toast.error(error?.message || "Failed to mark all notifications as read");
    }
  };

  const handleLoadMore = async () => {
    if (pagination.hasNext && !isLoading) {
      try {
        await loadMore();
      } catch (error: any) {
        console.error("Error loading more notifications:", error);
        toast.error("Failed to load more notifications");
      }
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.abs(now.getTime() - date.getTime()) / 36e5;

      if (diffInHours < 1) {
        return "Just now";
      } else if (diffInHours < 24) {
        return `${Math.floor(diffInHours)}h ago`;
      } else if (diffInHours < 48) {
        return "Yesterday";
      } else {
        return date.toLocaleDateString();
      }
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Unknown";
    }
  };

  // Handle error state
  if (error) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={variant === "admin" ? "ghost" : "outline"}
            size="icon"
            className={`relative ${variant === "admin" ? "" : "w-10 h-10 rounded-full bg-[#D9ECF8"} ${className}`}
          >
            <Bell className={variant === "admin" ? "h-5 w-5" : "h-6 w-6"} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className={
            variant === "admin"
              ? "w-80"
              : "min-w-[320px] max-h-96 overflow-y-auto bg-white"
          }
          align="end"
        >
          <div className="p-4 text-center">
            <p className="text-sm text-red-600">Failed to load notifications</p>
            <p className="text-xs text-gray-500 mt-1">{error.message}</p>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === "admin" ? "ghost" : "outline"}
          size="icon"
          className={`relative ${variant === "admin" ? "" : "w-10 h-10 rounded-full bg-[#D9ECF8]"} ${className}`}
        >
          <Bell
            className={`text-[#5D93A5] fill-[#5D93A5] ${variant === "admin" ? "h-5 w-5" : "h-6 w-6"}`}
          />
          {unreadCount > 0 && (
            <span
              className={`absolute ${
                variant === "admin"
                  ? "-top-1 -right-1 flex h-4 w-4"
                  : "top-[2px] right-[2px] w-2 h-2"
              } items-center justify-center rounded-full bg-red-500 text-xs text-white ${
                variant === "admin" ? "" : "animate-pulse"
              }`}
            >
              {variant === "admin" ? Math.min(unreadCount, 99) : ""}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={
          variant === "admin"
            ? "w-80 max-h-[500px] overflow-y-auto"
            : "min-w-[320px] max-w-[400px] max-h-[500px] overflow-y-auto bg-white"
        }
        align="end"
      >
        <div className="flex items-center justify-between">
          <DropdownMenuLabel className="text-base font-semibold">
            Notifications
            {unreadCount > 0 && (
              <span className="ml-2 text-sm text-muted-foreground">
                ({unreadCount} unread)
              </span>
            )}
          </DropdownMenuLabel>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-xs"
              disabled={isLoading}
            >
              <CheckCircle className="w-3 h-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <DropdownMenuSeparator />

        {isLoading && notifications.length === 0 ? (
          <div className="flex items-center justify-center p-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="ml-2 text-sm text-gray-500">Loading...</span>
          </div>
        ) : notifications.length > 0 ? (
          <>
            <div className="max-h-80 overflow-y-auto">
              {notifications.map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  className={`${
                    notification.status !== "READ"
                      ? variant === "admin"
                        ? "bg-blue-50"
                        : "font-semibold bg-[#D9ECF8] text-[#2B6A7F]"
                      : ""
                  } ${
                    variant === "admin" ? "py-2 group" : "py-4"
                  } cursor-pointer transition-all hover:bg-[#D9ECF8] hover:text-[#2B6A7F] focus:bg-[#D9ECF8] focus:text-[#2B6A7F]`}
                  onClick={() =>
                    notification.status !== "READ" &&
                    !isLoading &&
                    handleMarkAsRead(notification.id)
                  }
                >
                  <div className="flex items-start w-full">
                    <div className="flex-shrink-0 pt-0.5 mr-3">
                      {notification.status !== "READ" ? (
                        <Circle
                          className={`${
                            variant === "admin"
                              ? "h-2 w-2 rounded-full bg-[var(--primary-font)]"
                              : "w-4 h-4 text-[#2B6A7F]"
                          }`}
                        />
                      ) : (
                        <CheckCircle2
                          className={`${
                            variant === "admin"
                              ? "h-2 w-2 rounded-full bg-transparent text-green-600"
                              : "w-4 h-4 text-muted-foreground"
                          }`}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-start justify-start">
                            <p
                              className={`text-sm ${
                                variant === "admin"
                                  ? "text-gray-900 group-hover:!text-white"
                                  : notification.status !== "READ"
                                    ? "text-[#2B6A7F]"
                                    : ""
                              } font-medium truncate`}
                              title={notification.subject || "New Notification"}
                            >
                              {notification.subject || "New Notification"}
                            </p>
                            {notification.type && (
                              <span
                                className={`inline-block px-4 py-1 text-xs scale-75 rounded-full -translate-y-[3px] border ${
                                  notification.type === "URGENT"
                                    ? "bg-red-100 text-red-800 !border-red-800/25"
                                    : notification.type === "ALERT"
                                      ? "bg-orange-100 text-orange-800 !border-orange-800/25"
                                      : notification.type === "REMINDER"
                                        ? "bg-blue-100 text-blue-800 !border-blue-800/25"
                                        : notification.type === "COURSE"
                                          ? "bg-green-100 text-green-800 !border-green-800/25"
                                          : notification.type === "ADMIN"
                                            ? "bg-purple-100 text-purple-800 !border-purple-800/25"
                                            : notification.type === "WELCOME"
                                              ? "bg-yellow-100 text-yellow-800 !border-yellow-800/25"
                                              : "bg-gray-100 text-gray-800 !border-gray-800/25"
                                }`}
                              >
                                {notification.type}
                              </span>
                            )}
                          </div>
                          <p
                            className={`mt-1 text-xs ${
                              variant === "admin"
                                ? "text-gray-500 group-hover:!text-white"
                                : notification.status !== "READ"
                                  ? "text-[#2B6A7F]"
                                  : ""
                            } break-words whitespace-normal`}
                            title={notification.body}
                          >
                            {notification.body}
                          </p>
                        </div>
                        <span
                          className={`ml-2 text-xs ${
                            variant === "admin"
                              ? "text-gray-500 group-hover:!text-white"
                              : notification.status !== "READ"
                                ? "text-[#2B6A7F]"
                                : ""
                          } whitespace-nowrap`}
                        >
                          {formatDate(notification.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </div>

            {pagination.hasNext && (
              <>
                <DropdownMenuSeparator />
                <div className="p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLoadMore}
                    className="w-full text-sm"
                    disabled={isLoading}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    {isLoading ? "Loading..." : "Load more notifications"}
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          <DropdownMenuItem
            disabled
            className="text-sm bg-neutral-200 justify-center py-6"
          >
            No notifications found
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
